/**
 * What a QR code can hold, and the fields a customer fills in for each.
 *
 * A QR code is only ever a string, and every useful kind of code is a string in
 * some format a phone recognises - a vCard, a WIFI: line, a calendar event, a
 * wa.me link. Asking a customer to type those formats is asking them to get
 * them wrong: the old contact form wanted "one per line" and guessed which line
 * was the phone number. So each kind has its own fields, the fields are what
 * the design stores, and the string is built from them here and nowhere else.
 *
 * One module for the editor, the render worker and the tests, so what the
 * customer sees in the panel, what prints and what a phone reads cannot drift.
 */

export type QrKind =
  | 'url'
  | 'text'
  | 'vcard'
  | 'wifi'
  | 'email'
  | 'phone'
  | 'sms'
  | 'whatsapp'
  | 'location'
  | 'event'
  | 'social';

export type QrFieldValues = Record<string, string>;

export interface QrField {
  key: string;
  label: string;
  input: 'text' | 'url' | 'email' | 'tel' | 'textarea' | 'datetime' | 'select' | 'toggle';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  /** What the field starts as, for selects that must always hold something. */
  initial?: string;
}

export interface QrType {
  kind: QrKind;
  label: string;
  group: 'Links' | 'Contact' | 'Other';
  fields: QrField[];
}

export const SOCIAL_NETWORKS: Array<{ value: string; label: string; profile: ( handle: string ) => string }> = [
  { value: 'instagram', label: 'Instagram', profile: ( h ) => `https://instagram.com/${ h }` },
  { value: 'facebook', label: 'Facebook', profile: ( h ) => `https://facebook.com/${ h }` },
  { value: 'linkedin', label: 'LinkedIn', profile: ( h ) => `https://linkedin.com/in/${ h }` },
  { value: 'x', label: 'X (Twitter)', profile: ( h ) => `https://x.com/${ h }` },
  { value: 'tiktok', label: 'TikTok', profile: ( h ) => `https://tiktok.com/@${ h }` },
  { value: 'youtube', label: 'YouTube', profile: ( h ) => `https://youtube.com/@${ h }` },
  { value: 'snapchat', label: 'Snapchat', profile: ( h ) => `https://snapchat.com/add/${ h }` },
];

/*
 * Shared keys on purpose. A customer who typed a number for a phone-call code
 * and then decides a WhatsApp code is better should not type it again, so
 * `phone`, `message` and `place` mean the same thing in every kind that has
 * them.
 */
const PHONE: QrField = { key: 'phone', label: 'Phone number', input: 'tel', placeholder: '5555 1234', required: true };

export const QR_TYPES: QrType[] = [
  {
    kind: 'url',
    label: 'Web address',
    group: 'Links',
    fields: [ { key: 'url', label: 'Address', input: 'url', placeholder: 'prismatize.com', required: true } ],
  },
  {
    kind: 'social',
    label: 'Social profile',
    group: 'Links',
    fields: [
      {
        key: 'network',
        label: 'Network',
        input: 'select',
        required: true,
        initial: 'instagram',
        options: SOCIAL_NETWORKS.map( ( n ) => ( { value: n.value, label: n.label } ) ),
      },
      { key: 'handle', label: 'Username or profile link', input: 'text', placeholder: '@prismatprinting', required: true },
    ],
  },
  {
    kind: 'location',
    label: 'Location',
    group: 'Links',
    fields: [
      {
        key: 'place',
        label: 'Google Maps link or address',
        input: 'text',
        placeholder: 'https://maps.app.goo.gl/… or an address',
        required: true,
      },
    ],
  },
  {
    kind: 'vcard',
    label: 'Contact card',
    group: 'Contact',
    fields: [
      { key: 'name', label: 'Name', input: 'text', placeholder: 'Your full name', required: true },
      { key: 'company', label: 'Company', input: 'text' },
      { key: 'title', label: 'Job title', input: 'text' },
      { key: 'mobile', label: 'Mobile', input: 'tel', placeholder: '5555 1234' },
      { key: 'work', label: 'Work phone', input: 'tel' },
      { key: 'email', label: 'Email', input: 'email', placeholder: 'name@company.com' },
      { key: 'website', label: 'Website', input: 'url', placeholder: 'company.com' },
      { key: 'address', label: 'Address', input: 'textarea' },
    ],
  },
  { kind: 'phone', label: 'Phone call', group: 'Contact', fields: [ PHONE ] },
  {
    kind: 'whatsapp',
    label: 'WhatsApp',
    group: 'Contact',
    fields: [ PHONE, { key: 'message', label: 'Message (optional)', input: 'textarea', placeholder: 'Hello, I saw your card' } ],
  },
  {
    kind: 'sms',
    label: 'Text message',
    group: 'Contact',
    fields: [ PHONE, { key: 'message', label: 'Message (optional)', input: 'textarea' } ],
  },
  {
    kind: 'email',
    label: 'Email',
    group: 'Contact',
    fields: [
      { key: 'email', label: 'Email address', input: 'email', placeholder: 'name@company.com', required: true },
      { key: 'subject', label: 'Subject (optional)', input: 'text' },
      { key: 'body', label: 'Message (optional)', input: 'textarea' },
    ],
  },
  {
    kind: 'wifi',
    label: 'WiFi',
    group: 'Other',
    fields: [
      { key: 'ssid', label: 'Network name', input: 'text', required: true },
      { key: 'password', label: 'Password', input: 'text' },
      {
        key: 'security',
        label: 'Security',
        input: 'select',
        initial: 'WPA',
        options: [
          { value: 'WPA', label: 'WPA / WPA2 / WPA3' },
          { value: 'WEP', label: 'WEP (old routers)' },
          { value: 'nopass', label: 'No password' },
        ],
      },
      { key: 'hidden', label: 'Hidden network', input: 'toggle' },
    ],
  },
  {
    kind: 'event',
    label: 'Event',
    group: 'Other',
    fields: [
      { key: 'title', label: 'Event name', input: 'text', required: true },
      { key: 'start', label: 'Starts', input: 'datetime', required: true },
      { key: 'end', label: 'Ends', input: 'datetime' },
      { key: 'place', label: 'Place', input: 'text' },
      { key: 'notes', label: 'Details', input: 'textarea' },
    ],
  },
  {
    kind: 'text',
    label: 'Plain text',
    group: 'Other',
    fields: [ { key: 'text', label: 'Text', input: 'textarea', placeholder: 'Anything you like', required: true } ],
  },
];

export function qrType( kind: string ): QrType {
  return QR_TYPES.find( ( t ) => t.kind === kind ) ?? QR_TYPES[ 0 ]!;
}

/** A field's value, or what a select starts as when nothing was chosen yet. */
export function fieldValue( type: QrType, fields: QrFieldValues, key: string ): string {
  const def = type.fields.find( ( f ) => f.key === key );
  const own = ( fields[ key ] ?? '' ).trim();

  return own !== '' ? own : def?.initial ?? '';
}

/**
 * Only this kind's answers, for storing.
 *
 * Switching a code from WiFi to a web address carries shared answers across
 * (a phone number is a phone number), but what is saved is only what this
 * kind asks for - a WiFi password has no business riding along, unseen, in a
 * code that now points at a website.
 */
export function fieldsFor( kind: QrKind, fields: QrFieldValues ): QrFieldValues {
  const type = qrType( kind );
  const kept: QrFieldValues = {};

  for ( const field of type.fields ) {
    const raw = fields[ field.key ] ?? '';

    /*
     * Kept as typed, spaces and all. The settings panel saves on every
     * keystroke, and trimming here ate the space between "Jino" and "Joseph"
     * before the second word could be typed. The payload trims when it is
     * built, which is the only place it matters.
     */
    if ( raw.trim() !== '' ) kept[ field.key ] = raw;
    else if ( field.initial ) kept[ field.key ] = field.initial;
  }

  return kept;
}

/* ---------------------------------------------------------------
 * Phone numbers
 * ------------------------------------------------------------ */

/**
 * A number as a phone dials it, with its country code.
 *
 * The shop is in Qatar and so are most of its customers, who write their own
 * number the way they say it: eight digits. A WhatsApp or SMS code without a
 * country code opens nothing, so eight digits or fewer are taken as Qatari.
 * Anything longer is assumed to carry its own code already, and the panel shows
 * the number exactly as it will scan so a customer abroad can see it is right.
 */
export function phoneNumber( raw: string ): string {
  let typed = ( raw ?? '' ).trim();

  if ( typed === '' ) return '';

  typed = typed.replace( /[^\d+]/g, '' );

  if ( typed.startsWith( '00' ) ) typed = `+${ typed.slice( 2 ) }`;

  if ( typed.startsWith( '+' ) ) {
    const digits = typed.slice( 1 ).replace( /\D/g, '' );

    return digits ? `+${ digits }` : '';
  }

  const digits = typed.replace( /\D/g, '' );

  if ( digits === '' ) return '';

  return digits.length <= 8 ? `+974${ digits }` : `+${ digits }`;
}

/* ---------------------------------------------------------------
 * The string a phone reads
 * ------------------------------------------------------------ */

/** A web address a phone will open: a bare domain gets https. */
export function webAddress( raw: string ): string {
  const value = ( raw ?? '' ).trim();

  if ( value === '' ) return '';

  return /^[a-z][a-z0-9+.-]*:/i.test( value ) ? value : `https://${ value }`;
}

/** vCard and iCalendar text: backslash, comma, semicolon and newline escaped. */
function card( text: string ): string {
  return text.replace( /\\/g, '\\\\' ).replace( /[;,]/g, ( c ) => `\\${ c }` ).replace( /\r?\n/g, '\\n' );
}

function wifiText( text: string ): string {
  return text.replace( /([\\;,:"])/g, '\\$1' );
}

/**
 * "2026-09-13T09:30" as iCalendar wants it, with no time zone: floating time,
 * which a phone reads as local. A card handed out in Doha for an event in Doha
 * means Doha time, and a UTC stamp would move it three hours on every phone
 * whose clock was set anywhere else.
 */
function stamp( local: string, addHours = 0 ): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec( local.trim() );

  if ( ! m ) return '';

  const at = new Date( Date.UTC( +m[ 1 ]!, +m[ 2 ]! - 1, +m[ 3 ]!, +m[ 4 ]! + addHours, +m[ 5 ]! ) );
  const two = ( n: number ) => String( n ).padStart( 2, '0' );

  return `${ at.getUTCFullYear() }${ two( at.getUTCMonth() + 1 ) }${ two( at.getUTCDate() ) }T${ two( at.getUTCHours() ) }${ two(
    at.getUTCMinutes()
  ) }00`;
}

/**
 * The payload for a kind and its fields, or '' when something required is
 * missing - which is what the preflight check and the panel both read as "this
 * code holds nothing yet".
 */
export function buildPayload( kind: QrKind, fields: QrFieldValues ): string {
  const type = qrType( kind );
  const get = ( key: string ) => fieldValue( type, fields, key );

  if ( type.fields.some( ( f ) => f.required && get( f.key ) === '' ) ) return '';

  switch ( kind ) {
    case 'url':
      return webAddress( get( 'url' ) );

    case 'text':
      return get( 'text' );

    case 'phone': {
      const number = phoneNumber( get( 'phone' ) );

      return number ? `tel:${ number }` : '';
    }

    case 'sms': {
      const number = phoneNumber( get( 'phone' ) );

      // SMSTO: is the form both Android scanners and the iPhone camera read.
      return number ? `SMSTO:${ number }:${ get( 'message' ) }` : '';
    }

    case 'whatsapp': {
      const number = phoneNumber( get( 'phone' ) );
      const message = get( 'message' );

      if ( ! number ) return '';

      // wa.me wants the digits alone, without the plus.
      return `https://wa.me/${ number.slice( 1 ) }${ message ? `?text=${ encodeURIComponent( message ) }` : '' }`;
    }

    case 'email': {
      const query = [
        get( 'subject' ) ? `subject=${ encodeURIComponent( get( 'subject' ) ) }` : '',
        get( 'body' ) ? `body=${ encodeURIComponent( get( 'body' ) ) }` : '',
      ].filter( Boolean );

      return `mailto:${ get( 'email' ) }${ query.length ? `?${ query.join( '&' ) }` : '' }`;
    }

    case 'location': {
      const place = get( 'place' );

      // A link someone copied out of Maps opens exactly what they chose.
      if ( /^https?:\/\//i.test( place ) ) return place;
      if ( /^(maps\.app\.goo\.gl|goo\.gl\/maps|(www\.)?google\.[a-z.]+\/maps)/i.test( place ) ) return `https://${ place }`;

      // Coordinates, or an address Maps can search for.
      const query = /^-?\d{1,2}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?$/.test( place )
        ? place.replace( /\s+/g, '' )
        : encodeURIComponent( place );

      return `https://www.google.com/maps/search/?api=1&query=${ query }`;
    }

    case 'social': {
      const handle = get( 'handle' );

      // A pasted profile link is used as it is.
      if ( /^https?:\/\//i.test( handle ) ) return handle;
      if ( /^[a-z0-9-]+\.[a-z.]+\//i.test( handle ) ) return `https://${ handle }`;

      const network = SOCIAL_NETWORKS.find( ( n ) => n.value === get( 'network' ) ) ?? SOCIAL_NETWORKS[ 0 ]!;
      const name = handle.replace( /^@+/, '' ).replace( /\s+/g, '' );

      return name ? network.profile( encodeURIComponent( name ) ) : '';
    }

    case 'wifi': {
      const password = get( 'password' );
      const security = password === '' ? 'nopass' : get( 'security' ) || 'WPA';

      return [
        `WIFI:T:${ security };`,
        `S:${ wifiText( get( 'ssid' ) ) };`,
        security !== 'nopass' ? `P:${ wifiText( password ) };` : '',
        get( 'hidden' ) === 'yes' ? 'H:true;' : '',
        ';',
      ].join( '' );
    }

    case 'vcard': {
      const name = get( 'name' );
      const parts = name.split( /\s+/ );
      const last = parts.length > 1 ? parts.pop()! : '';
      const first = parts.join( ' ' );
      const mobile = phoneNumber( get( 'mobile' ) );
      const work = phoneNumber( get( 'work' ) );

      return [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `N:${ card( last ) };${ card( first ) };;;`,
        `FN:${ card( name ) }`,
        get( 'company' ) && `ORG:${ card( get( 'company' ) ) }`,
        get( 'title' ) && `TITLE:${ card( get( 'title' ) ) }`,
        mobile && `TEL;TYPE=CELL:${ mobile }`,
        work && `TEL;TYPE=WORK:${ work }`,
        get( 'email' ) && `EMAIL:${ get( 'email' ) }`,
        get( 'website' ) && `URL:${ webAddress( get( 'website' ) ) }`,
        get( 'address' ) && `ADR;TYPE=WORK:;;${ card( get( 'address' ) ) };;;;`,
        'END:VCARD',
      ]
        .filter( Boolean )
        .join( '\n' );
    }

    case 'event': {
      const start = stamp( get( 'start' ) );

      if ( ! start ) return '';

      // No end: an hour, which is what every calendar assumes too.
      const end = get( 'end' ) ? stamp( get( 'end' ) ) : stamp( get( 'start' ), 1 );

      return [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `SUMMARY:${ card( get( 'title' ) ) }`,
        `DTSTART:${ start }`,
        end && `DTEND:${ end }`,
        get( 'place' ) && `LOCATION:${ card( get( 'place' ) ) }`,
        get( 'notes' ) && `DESCRIPTION:${ card( get( 'notes' ) ) }`,
        'END:VEVENT',
        'END:VCALENDAR',
      ]
        .filter( Boolean )
        .join( '\n' );
    }
  }

  return '';
}

/* ---------------------------------------------------------------
 * Checks
 * ------------------------------------------------------------ */

/**
 * What looks wrong, in words - advice, not a wall.
 *
 * A code that is merely odd still scans; one with a typo in the email address
 * scans perfectly and sends every message nowhere, and that is the one worth
 * catching before a thousand cards are printed.
 */
export function qrProblems( kind: QrKind, fields: QrFieldValues ): string[] {
  const type = qrType( kind );
  const get = ( key: string ) => fieldValue( type, fields, key );
  const has = ( key: string ) => type.fields.some( ( f ) => f.key === key );
  const found: string[] = [];

  for ( const key of [ 'email' ] ) {
    const value = get( key );

    if ( has( key ) && value && ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( value ) ) {
      found.push( `“${ value }” doesn’t look like an email address.` );
    }
  }

  for ( const key of [ 'phone', 'mobile', 'work' ] ) {
    const value = get( key );

    if ( has( key ) && value && phoneNumber( value ).length < 8 ) {
      found.push( `“${ value }” looks too short to be a phone number.` );
    }
  }

  for ( const key of [ 'url', 'website' ] ) {
    const value = get( key );

    if ( has( key ) && value && /\s/.test( value ) ) found.push( 'A web address can’t have spaces in it.' );
  }

  if ( kind === 'event' && get( 'end' ) && get( 'start' ) && stamp( get( 'end' ) ) < stamp( get( 'start' ) ) ) {
    found.push( 'The event ends before it starts.' );
  }

  if ( kind === 'wifi' && get( 'security' ) !== 'nopass' && get( 'password' ) === '' && get( 'ssid' ) ) {
    found.push( 'No password given, so the code will join the network as an open one.' );
  }

  return found;
}

/* ---------------------------------------------------------------
 * Codes made before there were fields
 * ------------------------------------------------------------ */

/**
 * Fields for a code saved as one string, so the panel can show it as a form.
 *
 * Used for display only. The layer is not rewritten until the customer changes
 * something: a design that has been approved must print exactly the code that
 * was approved, and quietly rebuilding its vCard in a newer shape - however
 * equivalent - would change the press file behind their back.
 */
export function fieldsFromValue( kind: QrKind, value: string ): QrFieldValues {
  const text = ( value ?? '' ).trim();

  switch ( kind ) {
    case 'url':
      return { url: text };

    case 'wifi': {
      const [ ssid = '', password = '' ] = text.split( ':' );

      return { ssid, password, security: password ? 'WPA' : 'nopass' };
    }

    case 'vcard': {
      const [ name = '', ...rest ] = text.split( '\n' ).map( ( line ) => line.trim() ).filter( Boolean );
      const fields: QrFieldValues = { name };
      const phones = rest.filter( ( line ) => ! line.includes( '@' ) );

      fields.email = rest.find( ( line ) => line.includes( '@' ) ) ?? '';
      fields.mobile = phones[ 0 ] ?? '';
      fields.work = phones[ 1 ] ?? '';

      return fields;
    }

    default:
      return { text };
  }
}
